// Static content data for ThinkRight Tech website

export const NAV_LINKS = [
  { label: "Work", to: "/work" },
  { label: "Capabilities", to: "/services" },
  { label: "About", to: "/about" },
  { label: "Life", to: "/life" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

export const STATS = [
  { value: "08+", label: "Years engineering partner products" },
  { value: "120+", label: "Engineers, researchers & designers" },
  { value: "01", label: "Venture-backed partner scaled from 0 → 1" },
  { value: "10M+", label: "Records matched daily in production" },
];

export const CAPABILITIES = [
  {
    id: "applied-ai",
    number: "01",
    title: "Applied AI & GenAI",
    blurb:
      "We build production-grade LLM systems — retrieval, agents, fine-tuning and evaluation — that ship on real SLAs, not on demo days.",
    bullets: [
      "RAG & agentic systems",
      "Eval harnesses & guardrails",
      "Fine-tuning & distillation",
      "Voice & document AI",
    ],
  },
  {
    id: "ml-engineering",
    number: "02",
    title: "Machine Learning Engineering",
    blurb:
      "From entity resolution to ranking, we own the full ML lifecycle — data contracts, training, deployment, drift monitoring.",
    bullets: ["Entity resolution & MDM", "Ranking & recommendation", "Feature platforms", "MLOps & observability"],
  },
  {
    id: "data-engineering",
    number: "03",
    title: "Data & Platform Engineering",
    blurb:
      "High-throughput pipelines and clean contracts on Databricks, Snowflake and AWS — so models are trained on data you can trust.",
    bullets: ["Streaming + batch pipelines", "Lakehouse architectures", "Schema & contract design", "Cost & governance"],
  },
  {
    id: "product-engineering",
    number: "04",
    title: "Product Engineering",
    blurb:
      "Design-led React and TypeScript front-ends over FastAPI / Node services, shipped with the care of a product studio.",
    bullets: ["React + TypeScript", "Design systems", "Motion & accessibility", "API & SDK design"],
  },
  {
    id: "research",
    number: "05",
    title: "Research & Prototyping",
    blurb:
      "A standing cohort that de-risks hard ideas through rigorous prototyping — papers read, experiments run, findings shipped.",
    bullets: ["Rapid prototyping", "Evaluation studies", "Benchmarks & white papers", "Technical due diligence"],
  },
  {
    id: "devops",
    number: "06",
    title: "DevOps & Reliability",
    blurb:
      "Infra that rarely wakes anyone. Terraform, Kubernetes, CI/CD and SRE practices embedded from day one.",
    bullets: ["Terraform & Kubernetes", "CI/CD & DevSecOps", "SRE & incident response", "Cost optimization"],
  },
];

export const PARTNERS = [
  { name: "Matchbook.ai", slug: "matchbook-ai" },
  { name: "Databricks", slug: null },
  { name: "Salesforce AppExchange", slug: null },
  { name: "AWS Marketplace", slug: null },
  { name: "Microsoft Azure", slug: null },
];

export const CASE_STUDIES = [
  {
    slug: "matchbook-ai",
    name: "Matchbook.ai",
    tagline: "Autonomous data mastering for the Fortune 1000",
    sector: "B2B Data Intelligence · Enterprise SaaS",
    year: "2022 — present",
    hero_image:
      "https://images.pexels.com/photos/17483871/pexels-photo-17483871.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    cover_image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=80",
    metrics: [
      { value: "95%", label: "Real-time match accuracy" },
      { value: "10M+", label: "Records matched daily" },
      { value: "3,000+", label: "Attributes evaluated per record" },
      { value: "20%", label: "Dev-bandwidth freed via AI copilots" },
    ],
    challenge:
      "Enterprises carry decades of duplicated customer, vendor and supplier records across CRMs, ERPs, PIMs and third-party feeds like Dun & Bradstreet and ZoomInfo. Traditional MDM required armies of stewards and rule engines that aged badly. Matchbook needed a platform where matching, stewardship, enrichment and monitoring were autonomous — and explainable.",
    approach:
      "As Matchbook.ai's engineering backbone, we architected XDM — a real-time, code-free matching and stewardship platform — from the data plane up. Our teams built the entity-resolution engine, the explainable scoring layer, the Salesforce, Databricks and AWS Marketplace connectors, and the in-product AI copilot that now handles the long tail of user queries. We embedded AI into the steward workflow rather than bolting it on the side.",
    outcomes: [
      "Shipped Matchbook XDM from zero to a multi-cloud marketplace product on AWS, Azure, Databricks and Salesforce AppExchange",
      "Built a real-time entity-resolution engine achieving 95% accuracy on noisy enterprise feeds",
      "Reduced manual stewardship effort by double digits via autonomous rule suggestions and fine-tuned GenAI scoring",
      "Enabled a 20% reduction in engineering support load through an embedded AI copilot that resolves ~95% of in-app user questions",
    ],
    stack: [
      "Python · Spark · Databricks",
      "AWS (EKS, MSK, S3, Glue)",
      "PostgreSQL · pgvector",
      "Fine-tuned LLMs · RAG",
      "Salesforce Apex · Lightning",
      "Terraform · GitHub Actions",
    ],
    quote:
      "ThinkRight didn't just deliver engineers — they delivered a second product team. They own outcomes, not tickets.",
    quote_attrib: "VP Engineering · Matchbook.ai",
    why_us:
      "Matchbook.ai chose ThinkRight because this problem demanded product-grade engineering, not staff augmentation. Matching hundreds of millions of records with 95% accuracy is not a services project — it is a distributed-systems, ML and UX discipline practiced together. Our embedded model let their founders move at venture pace without rebuilding a GCC from scratch.",
  },
];

export const VALUES = [
  {
    n: "01",
    title: "Partner, not vendor",
    body: "We embed with founders and product leaders. Our wins are their wins; our mistakes are ours to own.",
  },
  {
    n: "02",
    title: "Research-grade rigor",
    body: "We read the paper. We run the experiment. We ship the finding. Rigor is a cultural default, not a ritual.",
  },
  {
    n: "03",
    title: "Craft over volume",
    body: "A small, senior team that owns outcomes will always beat a large team that owns tickets. We staff accordingly.",
  },
  {
    n: "04",
    title: "Clarity beats cleverness",
    body: "Architecture decision records, written narratives, and honest post-mortems — because clarity compounds.",
  },
];

export const LIFE_GALLERY = [
  {
    url: "https://images.pexels.com/photos/6804071/pexels-photo-6804071.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Diverse engineering team collaborating",
    span: "col-span-12 md:col-span-8 row-span-2",
  },
  {
    url: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80",
    alt: "Team meeting in modern office",
    span: "col-span-12 md:col-span-4",
  },
  {
    url: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80",
    alt: "Whiteboarding session",
    span: "col-span-6 md:col-span-4",
  },
  {
    url: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80",
    alt: "Product review",
    span: "col-span-6 md:col-span-4",
  },
  {
    url: "https://images.unsplash.com/photo-1515169067868-5387ec356754?auto=format&fit=crop&w=1200&q=80",
    alt: "Research lab",
    span: "col-span-12 md:col-span-8",
  },
  {
    url: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=80",
    alt: "Engineers pairing",
    span: "col-span-6 md:col-span-6",
  },
  {
    url: "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=1200&q=80",
    alt: "Architecture review",
    span: "col-span-6 md:col-span-6",
  },
];

export const BENEFITS = [
  { title: "Senior-heavy teams", body: "We deliberately staff mid-senior and above. You learn from builders who have shipped before, not from process." },
  { title: "Real equity of voice", body: "Architecture decisions, model choices, roadmap — ICs are in the room, and their reasoning is expected on paper." },
  { title: "Time for research", body: "A dedicated research cadence: reading groups, paper replications and an internal prototypes program." },
  { title: "Health & wellbeing", body: "Comprehensive medical, mental-health support through partners, and a no-surveillance hybrid policy." },
  { title: "Growth budget", body: "An annual learning stipend — conferences, courses, books, GPU credits — decided by you, not your manager." },
  { title: "Global exposure", body: "You ship to users in the US and EU. You sit with founders. You present to customers. No proxy work." },
];
